import { useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { upsertSignTemplateSubscription } from "@/lib/sign/signTemplateApiClient";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

/** TS-9 pilot stub — ShedCor publisher grants a dealer org access to a sign template family. */
export function ManufacturingSignTemplateSubscriptionsStub() {
  const [subscriberOrgId, setSubscriberOrgId] = useState("");
  const [templateFamilyId, setTemplateFamilyId] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleGrant() {
    if (!subscriberOrgId.trim() || !templateFamilyId.trim()) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const r = await upsertSignTemplateSubscription({
      subscriber_org_id: subscriberOrgId.trim(),
      template_family_id: templateFamilyId.trim(),
      mandatory,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMessage(r.data.revoked ? "Subscription revoked." : "Subscription granted.");
  }

  async function handleRevoke() {
    if (!subscriberOrgId.trim() || !templateFamilyId.trim()) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    const r = await upsertSignTemplateSubscription({
      subscriber_org_id: subscriberOrgId.trim(),
      template_family_id: templateFamilyId.trim(),
      revoke: true,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setMessage("Subscription revoked.");
  }

  return (
    <SectionCard
      title="Sign template subscriptions"
      description="Publisher admin grant for dealer orgs (ShedCor → Bennett pilot). API: POST /api/sign/subscription-upsert"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Subscriber org ID
          <input
            value={subscriberOrgId}
            onChange={(e) => setSubscriberOrgId(e.target.value)}
            placeholder="Bennett org UUID"
            className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Template family ID
          <input
            value={templateFamilyId}
            onChange={(e) => setTemplateFamilyId(e.target.value)}
            placeholder="Publisher template family UUID"
            className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
          />
        </label>
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
        <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} />
        Mandatory for subscriber send flows
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void handleGrant()}>
          {busy ? "Saving…" : "Grant subscription"}
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void handleRevoke()}>
          Revoke
        </button>
      </div>
      {error ? <p className="mt-2 text-xs text-red-600 dark:text-red-300">{error}</p> : null}
      {message ? <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">{message}</p> : null}
    </SectionCard>
  );
}
